/**
 * Split a markdown summary into key points, one per section.
 * Title = section heading, body = text until the next one, teaser = first sentence.
 * No sections → empty list (caller shows plain prose instead).
 *
 * The generator does not guarantee a format: the prompt only asks it to
 * "structure the summary into sections", so it emits either ATX headings
 * (`### Title`) or standalone bold lines (`**Title**`). Both are recognised.
 */

import { BOLD_TITLE, HEADING, stripClosingHashes } from './utils';

/** How a document marks its section titles. */
export type SummarySectionStyle = 'heading' | 'bold';

export interface SummaryKeyPoint {
  /** Stable id (title slug, de-duplicated) — used as the React list key. */
  id: string;
  /** Card title (the section heading). */
  title: string;
  /** First sentence of the body, shown while collapsed. */
  teaser: string;
  /** Markdown shown when expanded. */
  body: string;
  /** ATX level 1-6 the title was written at. Meaningless when style is `bold`. */
  level: number;
}

export type SummaryDocument = {
  /** Text before the first section. Dropped by `parseKeyPoints`, kept here. */
  preamble: string;
  /** The dialect this document uses, so edits can be written back in it. */
  style: SummarySectionStyle;
  points: SummaryKeyPoint[];
};

/** Default level for headings we write ourselves (new points, `bold` docs). */
export const DEFAULT_HEADING_LEVEL = 2;

/** Slugify a heading into a key-safe id (falls back for symbol-only titles). */
const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // collapse runs → single dashes
    .replace(/^-|-$/g, '') || 'key-point'; // strip the single edge dash

/** Strip markdown tokens so the teaser reads as plain text. */
const toPlainText = (markdown: string): string =>
  markdown
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/\*\*([^*]+)\*\*/g, '$1') // bold
    .replace(/\*([^*]+)\*/g, '$1') // italic
    .replace(/_([^_]+)_/g, '$1') // italic (underscore)
    .replace(/\[([^[\]]+)\]\([^()]*\)/g, '$1') // links → text (delimiters excluded → linear)
    .replace(/^[\s>*-]+/gm, '') // list/quote markers at line start
    .replace(/\s+/g, ' ')
    .trim();

const MAX_TEASER = 120;

const deriveTeaser = (body: string): string => {
  const plain = toPlainText(body);
  if (!plain) return '';
  // Take the first sentence (up to `.`/`!`/`?` + space).
  const [firstSentence] = plain.split(/(?<=[.!?])\s+/);
  const teaser = firstSentence ?? plain;
  return teaser.length > MAX_TEASER
    ? `${teaser.slice(0, MAX_TEASER - 1).trimEnd()}…`
    : teaser;
};

/** Count the leading `#`s of an ATX heading line. */
const headingLevel = (line: string): number =>
  (/^\s{0,3}(#{1,6})\s/.exec(line)?.[1] ?? '#').length;

/**
 * Pick the dialect for the whole document rather than per line: a document
 * that uses headings may well contain an emphasised line inside a body, and
 * that must not split the point in two.
 */
const detectStyle = (
  lines: string[],
  boldSections: boolean,
): SummarySectionStyle | null => {
  if (lines.some((line) => HEADING.test(line))) return 'heading';
  if (boldSections && lines.some((line) => BOLD_TITLE.test(line))) return 'bold';
  return null;
};

export type ParseSummaryOptions = {
  /**
   * Treat standalone bold lines as section titles.
   *
   * On for the editor, which has to surface every section the generator
   * produced. Off for `parseKeyPoints`, and so for the viewer: a bold-dialect
   * summary has always rendered as prose there, and quietly turning those
   * into an accordion would change published experiences nobody edited.
   */
  boldSections?: boolean;
};

/**
 * Parse a summary into its preamble, dialect and points — everything needed
 * to edit it and write it back unchanged. `parseKeyPoints` is the read-only
 * view of this used by the viewer.
 */
export function parseSummaryDocument(
  markdown: string,
  { boldSections = true }: ParseSummaryOptions = {},
): SummaryDocument {
  if (!markdown?.trim()) {
    return { preamble: '', style: 'heading', points: [] };
  }

  const lines = markdown.split('\n');
  const style = detectStyle(lines, boldSections);
  if (!style) {
    return { preamble: markdown.trim(), style: 'heading', points: [] };
  }

  const matcher = style === 'heading' ? HEADING : BOLD_TITLE;
  const points: SummaryKeyPoint[] = [];
  const idCounts = new Map<string, number>();
  const preambleLines: string[] = [];
  let title: string | null = null;
  let level = DEFAULT_HEADING_LEVEL;
  let bodyLines: string[] = [];

  const flush = () => {
    if (title === null) return;
    const body = bodyLines.join('\n').trim();
    // De-duplicate ids so repeated titles still get unique keys.
    const base = slugify(title);
    const seen = idCounts.get(base) ?? 0;
    idCounts.set(base, seen + 1);
    const id = seen === 0 ? base : `${base}-${seen + 1}`;
    points.push({ id, title, teaser: deriveTeaser(body), body, level });
  };

  for (const line of lines) {
    const match = matcher.exec(line);
    if (match) {
      flush();
      // Drop any ATX closing `##` run, then trim the space that preceded it.
      // A bold title carries its colon inside the markers (`**Overview:**`),
      // so strip that here rather than in the pattern. Headings keep theirs:
      // the viewer has always shown it.
      const raw = stripClosingHashes(match[1] ?? '');
      title = (style === 'bold' ? raw.replace(/:$/, '') : raw).trim();
      level = style === 'heading' ? headingLevel(line) : DEFAULT_HEADING_LEVEL;
      bodyLines = [];
    } else if (title !== null) {
      bodyLines.push(line);
    } else {
      preambleLines.push(line);
    }
  }
  flush();

  return { preamble: preambleLines.join('\n').trim(), style, points };
}

/**
 * The viewer's read-only view: headings only, so what it renders is exactly
 * what it rendered before the editor taught the parser about bold sections.
 */
export function parseKeyPoints(markdown: string): SummaryKeyPoint[] {
  return parseSummaryDocument(markdown, { boldSections: false }).points;
}
